<template>
  <div
    class="relative -mx-3 rounded py-3 px-3 hover:bg-gray-100"
    :class="{
      'text-gray-500': task.completedAt,
      'bg-gray-100': editingTitle || editingDescription,
    }"
  >
    <div class="items-center sm:flex">
      <div class="flex flex-1 items-center justify-between">
        <div class="mr-4 shrink-0">
          <a
            class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white"
            :class="toggledClasses"
            @click="toggleCompleted"
          >
            <span class="far fa-check text-lg" v-if="task.id"></span>
          </a>
        </div>
        <span
          class="block flex-1 cursor-text break-all"
          :class="{ 'text-gray-500': !task.title }"
          @click="focusTitle"
          v-if="!editingTitle"
          >{{ task.title || titlePlaceholder }}</span
        >
        <input
          class="task-title block flex-1 bg-transparent outline-none"
          :placeholder="titlePlaceholder"
          @blur="blurTitle"
          @keydown.enter="$event.target.blur()"
          @keydown.esc="$event.target.blur()"
          @keydown.delete="handleTitleDelete"
          v-model="task.title"
          v-if="editingTitle"
        />
      </div>
      <div
        v-if="showIcons"
        class="text-nowrap ml-12 flex flex-wrap items-center text-sm text-gray-500"
      >
        <span
          v-if="showDescriptionIcon"
          @click="focusDescription"
          class="mr-3 cursor-pointer hover:text-black"
          data-toggle="tooltip"
          :title="descriptionTooltip"
        >
          <span class="fal fa-align-left"></span>
        </span>
        <label
          class="mr-3"
          v-if="canUpdate || task.dueDate || task.completedAt"
          @click="toggleSettingDueDate"
          :class="{ 'cursor-pointer hover:text-black': !task.completedAt && canUpdate }"
          ref="dueDate"
        >
          <span
            class="fal"
            :class="{
              'fa-calendar-check': task.completedAt,
              'fa-calendar': !task.completedAt && task.dueDate,
              'fa-calendar-plus': !task.completedAt && !task.dueDate,
            }"
            data-toggle="tooltip"
            :title="dateTooltip"
          ></span>
          <span v-if="!settingDueDate">{{ formattedDate }}</span>
          <date-picker
            ref="dateInput"
            v-if="settingDueDate"
            class="bg-transparent"
            placeholder=""
            :value="task.rawDueDate"
            @change="setDueDate"
            @blur="setDueDate"
          ></date-picker>
        </label>
        <span
          v-if="showUserIcon"
          class="mr-3"
          :class="{
            'cursor-pointer hover:text-black':
              !task.completedAt && canUpdate && assignableUsers.length > 0,
          }"
          @click="toggleAssignUser"
        >
          <span
            class="fal"
            :class="{
              'fa-user-check': task.completedAt,
              'fa-user': !task.completedAt && userName,
              'fa-user-plus': !task.completedAt && !userName,
            }"
            data-toggle="tooltip"
            :title="userTooltip"
          ></span>
          {{ userName }}
        </span>
        <div v-if="showLink">
          <a
            :href="task.link"
            class="hover:text-black"
            data-toggle="tooltip"
            :title="task.linkDescription"
          >
            <span class="fal fa-search"></span>
            {{ $t('task.show_activity') }}
          </a>
        </div>
      </div>
    </div>
    <div
      class="ml-12 mt-2 flex text-sm leading-tight text-gray-500 sm:mt-1"
      v-show="!small && (task.description || this.editingDescription)"
    >
      <span
        class="task-description cursor-text whitespace-pre-line"
        :class="{ 'task-description-small': small }"
        v-text="task.description"
        @click="focusDescription"
        v-if="!this.editingDescription && task.description"
      />
      <textarea
        ref="descriptionTextarea"
        class="growing task-description flex-1 bg-transparent"
        :placeholder="$t('task.description_placeholder')"
        v-model="task.description"
        @blur="blurDescription"
        @keydown.esc="$event.target.blur()"
        v-show="this.editingDescription"
      ></textarea>
    </div>
    <div class="task-detail-input absolute shadow" v-if="assigningUser">
      <ul>
        <li
          class="px-3 py-1"
          v-for="user in assignableUsers"
          :key="user.id"
          @click="assignUser(user)"
        >
          <span class="fal fa-user-plus mr-1"></span>
          {{ user.name }}
        </li>
        <li class="px-3 py-1" v-if="task.assigneeId" @click="removeAssignedUser">
          <span class="fal fa-times mr-1"></span>
          {{ $t('task.remove_assigned_user') }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import GrowingTextarea from '../classes/GrowingTextarea'

export default {
  props: {
    task: Object,
    users: Array,
    readonly: {
      type: Boolean,
      default: false,
    },
    showLink: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      originalTitle: null,
      editingTitle: false,
      editingDescription: false,
      assigningUser: false,
      settingDueDate: false,
    }
  },
  methods: {
    toggleCompleted() {
      if (this.task.loading) {
        console.log('Still loading...')
        return
      }

      this.endEditing()
      this.$emit('toggle-completed')
    },
    focusTitle() {
      if (!this.canUpdate) {
        return
      }

      this.editingTitle = true
      this.originalTitle = this.task.title

      nextTick(() => {
        let element = this.$el.querySelector('input.task-title')
        if (element) {
          element.focus()
        }
      })
    },
    blurTitle() {
      this.editingTitle = false

      if (this.task.title === '') {
        if (!this.task.description) {
          this.$emit('delete')
        } else if (this.originalTitle) {
          this.task.title = this.originalTitle
        }
        return
      }

      this.originalTitle = null

      this.$emit('update-title')
    },
    handleTitleDelete() {
      if (this.task.title === '') {
        this.editingTitle = false
      }
    },
    focusDescription() {
      if (!this.canUpdate) {
        return
      }

      this.endEditing()
      this.editingDescription = true

      nextTick(() => {
        let element = this.$el.querySelector('textarea.task-description')
        if (element) {
          element.focus()
        }
      })
    },
    blurDescription() {
      this.editingDescription = false
      this.$emit('update-description')
    },
    toggleAssignUser() {
      if (!this.canUpdate || this.task.completedAt) {
        return
      }

      let value = !this.assigningUser
      this.endEditing()
      this.assigningUser = value
    },
    assignUser(user) {
      this.assigningUser = false
      this.$emit('assign-user', { task: this.task, user })
    },
    removeAssignedUser() {
      this.assigningUser = false
      this.$emit('assign-user', {
        task: this.task,
        user: {
          id: null,
        },
      })
    },
    toggleSettingDueDate() {
      if (!this.canUpdate || this.task.completedBy) {
        return
      }
      this.settingDueDate = true
    },
    cancelSettingDueDate() {
      this.settingDueDate = false
    },
    setDueDate(value) {
      if (!this.settingDueDate) {
        return
      }

      this.settingDueDate = false
      this.$emit('set-due-date', {
        task: this.task,
        dueDate: value ? moment(value).format('YYYY-MM-DD') : null,
      })
    },
    endEditing() {
      this.editingTitle = false
      this.editingDescription = false
      this.assigningUser = false
      this.settingDueDate = false
    },
  },
  computed: {
    toggledClasses() {
      let classes = { completed: this.task.completedAt, disabled: !this.task.id }

      if (!this.task.completedAt) {
        classes['border-2'] = true
        classes['text-transparent'] = true
        classes['hover:text-green-500'] = true
        classes['cursor-pointer'] = true
        classes['hover:border-green-500'] = true
      } else {
        classes['text-white'] = true
        classes['bg-green-500'] = true
      }

      return classes
    },
    formattedDate() {
      return this.task.completedAt || this.task.dueDate
    },
    userName() {
      if (this.task.completedByName || this.task.assigneeName) {
        return this.task.completedByName || this.task.assigneeName
      }

      let userId = this.task.completedBy || this.task.assigneeId
      if (!userId) {
        return null
      }

      let user = this.users.find((element) => {
        return element.id === userId
      })

      return user ? user.name : null
    },
    userTooltip() {
      if (this.task.completedAt) {
        return this.$t('task.tooltips.completed_by_user')
      }

      if (this.userName) {
        return this.$t('task.tooltips.assigned_to')
      }

      return this.$t('task.tooltips.assign_to')
    },
    dateTooltip() {
      if (this.task.completedAt) {
        return this.$t('task.tooltips.completed_at')
      }

      if (this.task.dueDate) {
        return this.$t('task.tooltips.due_by')
      }

      return this.$t('task.tooltips.set_due_date')
    },
    descriptionTooltip() {
      return this.$t('task.tooltips.description')
    },
    titlePlaceholder() {
      if (this.task.description) {
        return this.originalTitle || this.$t('task.title_placeholder')
      }
      return this.$t('task.title_placeholder')
    },
    showIcons() {
      return !!this.task.id
    },
    showDescriptionIcon() {
      return (
        this.canUpdate &&
        !this.task.completedAt &&
        this.task.title &&
        !this.task.description &&
        !this.editingDescription
      )
    },
    showUserIcon() {
      return (
        this.userName ||
        (this.users.length > 0 && (this.task.completedBy || this.task.assigneeId || this.canUpdate))
      )
    },
    assignableUsers() {
      return this.users.filter((user) => {
        return user.id !== this.task.assigneeId
      })
    },
    canUpdate() {
      return !this.readonly && (this.task.id ? !!this.task.canUpdate : true)
    },
  },
  mounted() {
    let descriptionTextarea = this.$refs.descriptionTextarea
    if (descriptionTextarea !== undefined) {
      descriptionTextarea.oninput = () => GrowingTextarea.grow(descriptionTextarea)
      descriptionTextarea.onfocus = () => GrowingTextarea.grow(descriptionTextarea)
      GrowingTextarea.grow(descriptionTextarea)
    }
  },
  emits: [
    'delete',
    'toggle-completed',
    'assign-user',
    'update-title',
    'update-description',
    'set-due-date',
  ],
}
</script>
